// Autocomplétion d'adresse et de code postal sur Google Places (New) — 17/10/2026.
//
// Le B2C passe sur le projet Google « eurekali-b2b », où Google interdit
// l'ancien widget `google.maps.places.Autocomplete`. Cette classe le remplace
// avec la même façon de s'en servir (addListener('place_changed'), getPlace(),
// setComponentRestrictions) : chaque écran ne change que d'une ligne.
// Si Google refuse ou ne répond pas, le champ reste une saisie libre : il n'est
// jamais désactivé.

const CLASSE_MENU = 'ek-ac'
const DELAI_MS = 250
const MIN_CARACTERES = 2

// Refus de clé : on arrête d'interroger Google pour toute la page.
let googleRefuse = false

// « be », ["BE", "de"], undefined → ['be', 'de'] (codes pays à 2 lettres, 15 au plus).
export function regionsDepuis(country) {
  const liste = Array.isArray(country) ? country : [country]
  return liste
    .filter((c) => typeof c === 'string' && /^[A-Za-z]{2}$/.test(c))
    .map((c) => c.toLowerCase())
    .slice(0, 15)
}

// Composants Places (New) → forme de l'ancien widget, que lisent tous les écrans.
export function versComposantsLegacy(composants) {
  return (composants || []).map((c) => ({
    long_name: c.longText || '',
    short_name: c.shortText || '',
    types: c.types || [],
  }))
}

export function estUnRefus(erreur) {
  return /REQUEST_DENIED|PERMISSION_DENIED|API_KEY|not activated|are blocked|ApiNotActivated|RefererNotAllowed|InvalidKey/i
    .test(String((erreur && (erreur.message || erreur.code)) || erreur))
}

function texteDe(prediction) {
  const t = prediction && prediction.text
  return (t && (t.text || t.toString())) || ''
}

async function bibliothequePlaces(win) {
  const maps = win.google && win.google.maps
  if (!maps) throw new Error('Google Maps absent')
  if (maps.places && maps.places.AutocompleteSuggestion) return maps.places
  if (maps.importLibrary) return maps.importLibrary('places')
  throw new Error('Places (New) indisponible')
}

function injecterStyle(doc) {
  if (doc.getElementById('ek-ac-style')) return
  const style = doc.createElement('style')
  style.id = 'ek-ac-style'
  style.textContent =
    '.ek-ac{position:absolute;z-index:10000;background:#fff;border:1px solid #E5097F;border-radius:14px;' +
    'box-shadow:0 6px 18px rgba(0,0,0,.12);max-height:280px;overflow:auto;font-size:14px;text-align:left}' +
    '.ek-ac .ek-ac-item{padding:9px 14px;cursor:pointer;color:#222}' +
    '.ek-ac .ek-ac-item.actif,.ek-ac .ek-ac-item:hover{background:#FEEEF6}' +
    '.ek-ac .ek-ac-info{padding:7px 14px;color:#666;font-style:italic;cursor:default}' +
    '.ek-ac .ek-ac-google{padding:4px 14px 6px;font-size:13px;color:#777;text-align:right}' +
    '.ek-ac .ek-ac-google a{color:#777}' +
    '.ek-ac-alerte{position:absolute;z-index:10000;background:#fff;border:1px solid #C62828;color:#C62828;' +
    'border-radius:10px;padding:6px 12px;font-size:13px;text-align:left}'
  doc.head.appendChild(style)
}

export class AutocompletionEk {
  constructor(champ, options = {}, win = window) {
    AutocompletionEk.detacher(champ)
    this.champ = champ
    this.win = win
    this.doc = champ.ownerDocument || win.document
    this.types = options.types && options.types.length ? options.types.slice(0, 5) : undefined
    this.regions = regionsDepuis(options.componentRestrictions && options.componentRestrictions.country)
    // Source des suggestions : Google par défaut, ou une source maison (19/10/2026 :
    // annuaire des codes postaux Eurêkali) — async (texte, regions) => [{ texte, composants }].
    this.fournisseur = typeof options.fournisseur === 'function' ? options.fournisseur : null
    // Mention sous les suggestions de cette source : texte, ou { texte, lien }.
    this.mention = options.mention || null
    // Champ « code postal » : (texte, regions, listeMontree) => null (accepté), { code }
    // (remis au propre) ou { message } (refusé, champ vidé) — voir verifierSaisie().
    this.verifier = typeof options.verifier === 'function' ? options.verifier : null
    this.ecouteurs = []
    this.suggestions = []
    this.listeMontree = false
    this.bulle = null
    this.retirerClicBulle = null
    this.attente = null
    this.actif = -1
    this.lieu = {}
    this.jeton = null
    this.minuterie = null
    this.numero = 0
    this.menu = null

    this.surSaisie = () => { this.fermerAlerte(); this.planifier() }
    this.surEntree = () => this.annulerAttente()
    this.surTouche = (e) => this.clavier(e)
    this.surSortie = (e) => {
      // Quitter le champ annule la recherche en attente ET la réponse déjà partie.
      this.win.clearTimeout(this.minuterie)
      this.numero++
      this.jeton = null
      const vers = (e && e.relatedTarget) || null
      this.win.setTimeout(() => this.apresSortie(vers), 150)
    }
    champ.addEventListener('input', this.surSaisie)
    champ.addEventListener('focus', this.surEntree)
    champ.addEventListener('keydown', this.surTouche)
    champ.addEventListener('blur', this.surSortie)
    if (champ.setAttribute) champ.setAttribute('autocomplete', 'off')
    champ.__ekAutocompletion = this
  }

  // Remplace `google.maps.event.clearInstanceListeners(champ)` des écrans.
  static detacher(champ) {
    const ancienne = champ && champ.__ekAutocompletion
    if (ancienne) ancienne.detruire()
  }

  detruire() {
    this.champ.removeEventListener('input', this.surSaisie)
    this.champ.removeEventListener('focus', this.surEntree)
    this.champ.removeEventListener('keydown', this.surTouche)
    this.champ.removeEventListener('blur', this.surSortie)
    this.win.clearTimeout(this.minuterie)
    this.numero++
    this.ecouteurs = []
    this.fermer()
    this.fermerAlerte()
    this.annulerAttente()
    if (this.champ.__ekAutocompletion === this) delete this.champ.__ekAutocompletion
  }

  // Même interface que l'ancien widget.
  setComponentRestrictions(restrictions) { this.regions = regionsDepuis(restrictions && restrictions.country) }
  setTypes(types) { this.types = types && types.length ? types.slice(0, 5) : undefined }
  setOptions(options = {}) {
    if (options.types) this.setTypes(options.types)
    if (options.componentRestrictions) this.setComponentRestrictions(options.componentRestrictions)
  }
  setFields() {}
  bindTo() {}
  getPlace() { return this.lieu }
  addListener(nom, rappel) {
    const ecouteur = { nom, rappel }
    this.ecouteurs.push(ecouteur)
    return { remove: () => { this.ecouteurs = this.ecouteurs.filter((e) => e !== ecouteur) } }
  }

  planifier() {
    this.win.clearTimeout(this.minuterie)
    const texte = (this.champ.value || '').trim()
    if ((googleRefuse && !this.fournisseur) || texte.length < MIN_CARACTERES) {
      this.listeMontree = false
      this.fermer()
      return
    }
    this.minuterie = this.win.setTimeout(() => this.chercher(texte), DELAI_MS)
  }

  async chercher(texte) {
    const numero = ++this.numero
    if (this.fournisseur) {
      let resultats
      try {
        resultats = await this.fournisseur(texte, this.regions)
      } catch (erreur) {
        this.listeMontree = false
        this.fermer()
        return
      }
      if (!this.toujoursActuelle(numero)) return
      // null : la source ne couvre pas ce pays (Royaume-Uni, Pays-Bas…) → Google.
      if (resultats !== null) {
        this.montrer((resultats || []).map((r) => ({ text: { text: r.texte }, composants: r.composants, info: r.info })))
        return
      }
      if (googleRefuse) { this.listeMontree = false; this.fermer(); return }
    }
    try {
      const places = await bibliothequePlaces(this.win)
      if (!this.jeton && places.AutocompleteSessionToken) this.jeton = new places.AutocompleteSessionToken()
      const requete = { input: texte }
      if (this.jeton) requete.sessionToken = this.jeton
      if (this.regions.length) requete.includedRegionCodes = this.regions
      if (this.types) requete.includedPrimaryTypes = this.types
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(requete)
      if (!this.toujoursActuelle(numero)) return
      this.montrer((suggestions || []).map((s) => s.placePrediction).filter(Boolean))
    } catch (erreur) {
      if (estUnRefus(erreur)) googleRefuse = true
      this.listeMontree = false
      this.fermer()
    }
  }

  // Une réponse de Google n'est affichée que si personne n'a tapé, quitté le champ
  // ou rebranché une autre autocomplétion entre-temps (relecture du 17/10).
  toujoursActuelle(numero) {
    if (numero !== this.numero || this.champ.__ekAutocompletion !== this) return false
    const doc = this.doc
    return !('activeElement' in doc) || doc.activeElement === this.champ
  }

  async choisir(index) {
    const prediction = this.suggestions[index]
    if (!prediction || prediction.info || this.champ.__ekAutocompletion !== this) return
    this.annulerAttente()
    this.fermer()
    this.champ.value = texteDe(prediction) || this.champ.value
    if (prediction.composants) {
      this.lieu = { address_components: prediction.composants, formatted_address: texteDe(prediction) }
      this.annoncerChoix()
      return
    }
    try {
      const lieu = prediction.toPlace()
      await lieu.fetchFields({ fields: ['addressComponents', 'formattedAddress'] })
      this.lieu = {
        address_components: versComposantsLegacy(lieu.addressComponents),
        formatted_address: lieu.formattedAddress || '',
      }
    } catch (erreur) {
      this.lieu = {}
    }
    this.jeton = null // la session Google se termine avec la sélection
    this.annoncerChoix()
  }

  // Champ « code postal » : après un choix, le champ montre le code seul (« SW1A 1AA » et
  // non « London SW1A 1AA, UK »), celui que l'écran enregistre (3e relecture du 19/10/2026).
  annoncerChoix() {
    if (this.verifier) {
      const cp = (this.lieu.address_components || []).find((c) => (c.types || []).includes('postal_code'))
      if (cp && cp.long_name) this.champ.value = cp.long_name
    }
    this.ecouteurs.filter((e) => e.nom === 'place_changed').forEach((e) => e.rappel())
  }

  montrer(suggestions) {
    this.suggestions = suggestions
    this.listeMontree = suggestions.some((s) => !s.info)
    this.afficher()
  }

  afficher() {
    this.fermer()
    if (!this.suggestions.length) return
    injecterStyle(this.doc)
    const menu = this.doc.createElement('div')
    menu.className = CLASSE_MENU
    this.suggestions.forEach((prediction, i) => {
      const ligne = this.doc.createElement('div')
      // Ligne d'information (« … et d'autres ») : toujours la dernière, jamais choisie.
      ligne.className = prediction.info ? 'ek-ac-info' : 'ek-ac-item'
      ligne.textContent = texteDe(prediction)
      // mousedown (et non click) : il passe avant la sortie du champ.
      ligne.addEventListener('mousedown', (e) => { e.preventDefault(); if (!prediction.info) this.choisir(i) })
      menu.appendChild(ligne)
    })
    const pied = this.doc.createElement('div')
    pied.className = 'ek-ac-google'
    const mention = this.suggestions.some((s) => s.composants) ? this.mention : null
    const texte = (mention && (mention.texte || mention)) || 'powered by Google'
    if (mention && mention.lien) {
      const lien = this.doc.createElement('a')
      lien.href = mention.lien
      lien.target = '_blank'
      lien.rel = 'noopener'
      lien.textContent = texte
      lien.addEventListener('mousedown', (e) => e.preventDefault()) // le champ garde la main
      pied.appendChild(lien)
    } else {
      pied.textContent = texte
    }
    menu.appendChild(pied)
    const r = this.champ.getBoundingClientRect()
    menu.style.left = `${r.left + (this.win.scrollX || 0)}px`
    menu.style.top = `${r.bottom + (this.win.scrollY || 0) + 4}px`
    menu.style.width = `${Math.max(r.width, 220)}px`
    this.doc.body.appendChild(menu)
    this.menu = menu
    this.actif = -1
  }

  clavier(e) {
    if (!this.menu) return
    const lignes = this.menu.querySelectorAll('.ek-ac-item')
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const pas = e.key === 'ArrowDown' ? 1 : -1
      this.actif = (this.actif + pas + lignes.length) % lignes.length
      lignes.forEach((l, i) => l.classList.toggle('actif', i === this.actif))
    } else if (e.key === 'Enter' && this.actif >= 0) {
      e.preventDefault()
      this.choisir(this.actif)
    } else if (e.key === 'Escape') {
      this.fermer()
    }
  }

  fermer() {
    if (this.menu && this.menu.parentNode) this.menu.parentNode.removeChild(this.menu)
    this.menu = null
    this.actif = -1
  }

  // Le client a quitté le champ : Tab, clic ailleurs, autre onglet, « Terminé » du clavier.
  apresSortie(vers) {
    if (this.champ.__ekAutocompletion !== this) return
    this.annulerAttente()
    if (!this.verifier) { this.fermer(); return }
    // Autre onglet, lien de licence, autre application : rien n'est effacé. On vérifie au
    // retour, sauf si le client revient dans le champ (2e relecture du 19/10/2026).
    if (this.doc.hasFocus && !this.doc.hasFocus()) {
      this.fermer()
      this.attendre(this.win, 'focus', false, () => this.win.setTimeout(() => {
        if (this.doc.activeElement !== this.champ) this.verifierSaisie()
      }, 0))
      return
    }
    // Revenu dans le champ entre-temps (après le cas « autre onglet » : le navigateur y
    // considère encore le champ comme actif — relecture finale du 19/10/2026).
    if (this.doc.activeElement === this.champ) return
    // Téléphone : « Terminé » ferme le clavier sans aller ailleurs. La liste reste ouverte
    // pour qu'on puisse y choisir ; la vérification attend le prochain toucher ailleurs.
    if (!vers && this.menu && this.ecranTactile()) {
      this.attendre(this.doc, 'pointerdown', true, (e) => {
        if (this.menu && this.menu.contains(e.target)) return false // choix dans la liste
        if (e.target !== this.champ) { this.fermer(); this.verifierSaisie() }
        return true
      })
      return
    }
    this.fermer()
    this.verifierSaisie()
  }

  ecranTactile() {
    return Boolean(this.win.matchMedia && this.win.matchMedia('(pointer: coarse)').matches)
  }

  // Un seul écouteur en attente à la fois ; il se retire dès que le rappel ne renvoie pas false.
  attendre(cible, evenement, capture, rappel) {
    this.annulerAttente()
    const ecouteur = (e) => { if (rappel(e) !== false) this.annulerAttente() }
    cible.addEventListener(evenement, ecouteur, capture)
    this.attente = () => cible.removeEventListener(evenement, ecouteur, capture)
  }

  annulerAttente() {
    if (this.attente) this.attente()
    this.attente = null
  }

  // Un code postal faux ne doit jamais partir (relectures du 19/10/2026) : la saisie est
  // acceptée, remise au propre (« 75001 Paris » → « 75001 »), ou refusée et effacée.
  // Aucun choix automatique : « Charleroi » ne doit pas devenir 6000 sans que le client le voie.
  verifierSaisie() {
    if (!this.verifier || this.champ.__ekAutocompletion !== this) return
    const texte = this.champ.value || ''
    if (!texte.trim()) return
    const verdict = this.verifier(texte, this.regions, this.listeMontree)
    if (!verdict) return
    this.champ.value = verdict.code || ''
    this.champ.dispatchEvent(new this.win.Event('input', { bubbles: true })) // prévient le v-model
    if (!verdict.code) this.afficherAlerte(verdict.message)
  }

  afficherAlerte(message) {
    this.fermerAlerte()
    injecterStyle(this.doc)
    const bulle = this.doc.createElement('div')
    bulle.className = 'ek-ac-alerte'
    bulle.setAttribute('role', 'alert')
    bulle.textContent = message
    const r = this.champ.getBoundingClientRect()
    bulle.style.left = `${r.left + (this.win.scrollX || 0)}px`
    bulle.style.top = `${r.bottom + (this.win.scrollY || 0) + 4}px`
    bulle.style.width = `${Math.max(r.width, 220)}px`
    this.doc.body.appendChild(bulle)
    this.bulle = bulle
    // Elle part au premier clic ou après 6 s, pour ne pas rester sur l'écran suivant.
    const auClic = () => this.fermerAlerte()
    this.doc.addEventListener('pointerdown', auClic, true)
    this.retirerClicBulle = () => this.doc.removeEventListener('pointerdown', auClic, true)
    this.win.setTimeout(() => { if (this.bulle === bulle) this.fermerAlerte() }, 6000)
  }

  fermerAlerte() {
    if (this.retirerClicBulle) this.retirerClicBulle()
    this.retirerClicBulle = null
    if (this.bulle && this.bulle.parentNode) this.bulle.parentNode.removeChild(this.bulle)
    this.bulle = null
  }
}

// Pour les tests uniquement.
export function _reinitialiserPourTests() { googleRefuse = false }
